import { useTranslation, Trans } from "react-i18next";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  TasksMockup,
  ChatMockup,
  AnnouncementsMockup,
  MotivationMockup,
  PersonalMockup,
  PlatformMockup,
  PushMockup,
  OrgChartMockup,
  OnboardingMockup,
  MobileMockup,
} from "./FeatureMockups";

/**
 * Alternating left/right feature sections. Each block has an icon-tagged eyebrow,
 * bold title, description, checkmark bullets and a domain-specific mockup.
 */
export function LandingFeatures() {
  const { t } = useTranslation();

  const items = [
    { id: "tasks", eyebrow: "01 · Görevler", Mockup: TasksMockup, tint: "violet" },
    { id: "chat", eyebrow: "02 · İletişim", Mockup: ChatMockup, tint: "sky", flip: true },
    { id: "push", eyebrow: "03 · Push Bildirimleri", Mockup: PushMockup, tint: "emerald" },
    { id: "orgchart", eyebrow: "04 · Org Chart", Mockup: OrgChartMockup, tint: "indigo", flip: true },
    { id: "announcements", eyebrow: "05 · Duyurular", Mockup: AnnouncementsMockup, tint: "amber" },
    { id: "motivation", eyebrow: "06 · Motivasyon", Mockup: MotivationMockup, tint: "pink", flip: true },
    { id: "onboarding", eyebrow: "07 · Onboarding", Mockup: OnboardingMockup, tint: "violet" },
    { id: "mobile", eyebrow: "08 · Mobil", Mockup: MobileMockup, tint: "sky", flip: true },
    { id: "personal", eyebrow: "09 · Kişisel Odak", Mockup: PersonalMockup, tint: "emerald" },
    { id: "platform", eyebrow: "10 · Kurumsal", Mockup: PlatformMockup, tint: "indigo", flip: true },
  ];

  return (
    <section
      id="features"
      className="relative bg-white"
      data-testid="landing-features"
    >
      <div className="mx-auto max-w-7xl px-6 pb-24 pt-20 lg:px-8">
        <div className="max-w-3xl">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-violet-200 bg-violet-50 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-violet-700">
            Özellikler
          </div>
          <h2 className="font-display text-3xl font-extrabold tracking-tight text-zinc-950 md:text-5xl">
            {t("landing.features_title")}
            <span className="serpix-gradient-text">
              {t("landing.features_title_accent")}
            </span>
            {t("landing.features_title_suffix")}
          </h2>
          <p className="mt-4 max-w-2xl text-base text-zinc-600 md:text-lg">
            {t("landing.features_sub")}
          </p>
        </div>

        <div className="mt-16 space-y-24 md:mt-24 md:space-y-32">
          {items.map((it) => (
            <FeatureRow key={it.id} {...it} />
          ))}
        </div>
      </div>
    </section>
  );
}

const tintClasses = {
  violet: {
    dot: "bg-violet-500",
    chip: "border-violet-200 bg-violet-50 text-violet-700",
    tick: "text-violet-600",
  },
  sky: {
    dot: "bg-sky-500",
    chip: "border-sky-200 bg-sky-50 text-sky-700",
    tick: "text-sky-600",
  },
  amber: {
    dot: "bg-amber-500",
    chip: "border-amber-200 bg-amber-50 text-amber-700",
    tick: "text-amber-600",
  },
  pink: {
    dot: "bg-pink-500",
    chip: "border-pink-200 bg-pink-50 text-pink-700",
    tick: "text-pink-600",
  },
  emerald: {
    dot: "bg-emerald-500",
    chip: "border-emerald-200 bg-emerald-50 text-emerald-700",
    tick: "text-emerald-600",
  },
  indigo: {
    dot: "bg-indigo-500",
    chip: "border-indigo-200 bg-indigo-50 text-indigo-700",
    tick: "text-indigo-600",
  },
};

function FeatureRow({ id, eyebrow, Mockup, tint, flip }) {
  const { t } = useTranslation();
  const bullets = t(`landing.features.${id}.bullets`, { returnObjects: true });
  const tc = tintClasses[tint];

  return (
    <div
      className="grid grid-cols-1 items-center gap-12 md:grid-cols-12 md:gap-16"
      data-testid={`landing-feature-${id}`}
    >
      <div className={cn("md:col-span-6", flip && "md:order-2")}>
        <div
          className={cn(
            "inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-wider",
            tc.chip
          )}
        >
          <span className={cn("h-1.5 w-1.5 rounded-full", tc.dot)} />
          {eyebrow}
        </div>
        <h3 className="mt-4 font-display text-2xl font-extrabold tracking-tight text-zinc-950 md:text-4xl">
          {t(`landing.features.${id}.title`)}
        </h3>
        <p className="mt-4 max-w-md text-base leading-relaxed text-zinc-600 md:text-lg">
          {t(`landing.features.${id}.desc`)}
        </p>
        <ul className="mt-6 space-y-3">
          {Array.isArray(bullets) &&
            bullets.map((b, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-zinc-700 md:text-base">
                <span
                  className={cn(
                    "mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-full bg-white shadow-sm ring-1 ring-inset ring-zinc-200",
                    tc.tick
                  )}
                >
                  <Check className="h-3 w-3" strokeWidth={3} />
                </span>
                <span>{b}</span>
              </li>
            ))}
        </ul>
      </div>

      <div className={cn("md:col-span-6", flip && "md:order-1")}>
        <div className="relative">
          <div className="pointer-events-none absolute -inset-4 rounded-3xl bg-gradient-to-br from-violet-100 via-white to-pink-100 opacity-70 blur-2xl" />
          <div className="relative">
            <Mockup />
          </div>
        </div>
      </div>
    </div>
  );
}
